import type { Optional } from '@festina-life/core'
import { Alert } from '@festina-life/ui/feedback'
import { Select } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { ConfirmationBox } from '@festina-life/ui/structure'

type Props = {
  isOpen: boolean
  onAccept: () => void
  onCancel: () => void
  operationName: string
  versionCount: number
  sourceBlock: string
  description?: string
  conflictWarning?: string
  children?: React.ReactNode
  ancestors: string[]
  ancestorValue: Optional<string>
  onChangeAncestor: (ancestor: string) => void
}

export const TransferConfirmationModal = ({
  isOpen,
  onAccept,
  onCancel,
  operationName,
  versionCount,
  sourceBlock,
  description,
  conflictWarning,
  children,
  ancestors,
  ancestorValue,
  onChangeAncestor,
}: Props) => {
  const translate = useTranslate()

  const handleChangedAncestor = (newAncestor: Optional<string>) => {
    if (newAncestor) {
      onChangeAncestor(newAncestor)
    }
  }

  return (
    <ConfirmationBox
      isOpen={isOpen}
      onCancel={onCancel}
      title={translate('transfer.confirmationModal.title', {
        itemId: operationName,
      })}
      onAccept={onAccept}
      confirmLabel={translate('transfer.confirmationModal.confirm')}
      panelClassName="m-auto max-w-lg"
      isButtonDisabled={!ancestorValue}
    >
      <div className="flex flex-col gap-2">
        <div className="grid grid-cols-[auto_1fr] gap-2">
          <div>
            {translate('transfer.confirmationModal.versionCount.label')}
          </div>
          <div>{versionCount}</div>
          <div>{translate('transfer.confirmationModal.sourceBlock.label')}</div>
          <div>{sourceBlock}</div>
        </div>
        <Select
          options={ancestors}
          value={ancestorValue}
          onChange={handleChangedAncestor}
          wrapperProps={{
            label: translate('transfer.ancestorBlockSelect.label'),
          }}
          shouldShowClearButton={false}
        />
        {children}
        {conflictWarning && <Alert severity="warning">{conflictWarning}</Alert>}
        <p>
          {description ??
            translate('transfer.confirmationModal.description', {
              sourceBlock,
            })}
        </p>
      </div>
    </ConfirmationBox>
  )
}
